import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDUSTRIES = [
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'it_tech', label: 'IT / Technology' },
  { value: 'finance', label: 'Finance' },
  { value: 'education', label: 'Education' },
  { value: 'retail', label: 'Retail' },
  { value: 'hr', label: 'Human Resources' },
];

// Apple-style nav
const AppleNav = () => (
  <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/75 border-b border-black/5">
    <div className="max-w-[1100px] mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0071e3 0%, #005ec7 100%)' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true">
              <circle cx="9" cy="12" r="3.5" />
              <path d="M15 8 Q 19 6, 21 4" />
              <path d="M14.5 12 L 22 12" />
              <path d="M15 16 Q 19 18, 21 20" />
            </svg>
        </div>
        <span className="text-[17px] font-bold tracking-tight text-[#1d1d1f] lowercase">mindpulsei</span>
      </Link>
      <div className="hidden md:flex items-center gap-7">
        <Link to="/#pillars" className="text-[12px] text-[#424245] hover:text-[#1d1d1f]">Pillars</Link>
        <Link to="/emotions" className="text-[12px] text-[#424245] hover:text-[#1d1d1f]">Library</Link>
        <Link to="/#organizations" className="text-[12px] text-[#1d1d1f]">Organizations</Link>
      </div>
      <Link to="/login" className="text-[12px] text-current-teal hover:text-ice-mist">
        Sign in ›
      </Link>
    </div>
  </nav>
);

// Apple-style input
const AppleInput = ({ label, id, tid, ...props }) => (
  <div className="space-y-2">
    <label htmlFor={id} className="text-[13px] font-medium text-[#1d1d1f] tracking-tight block">{label}</label>
    <input
      id={id}
      data-testid={tid}
      className="w-full px-4 py-3 rounded-xl bg-[#f5f5f7] border border-transparent text-[15px] text-[#1d1d1f] placeholder:text-[#86868b] focus:border-current-teal focus:bg-white focus:outline-none transition-colors"
      {...props}
    />
  </div>
);

const CorporateRegister = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    industry: '',
    admin_name: '',
    admin_email: '',
    admin_password: '',
    confirm_password: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.admin_password !== form.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (form.admin_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (!form.industry) {
      toast.error('Please select an industry');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API}/corporate/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          industry: form.industry,
          admin_name: form.admin_name,
          admin_email: form.admin_email,
          admin_password: form.admin_password,
        }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Registration failed');
      }
      toast.success('Organization registered successfully!');
      navigate('/');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-[#1d1d1f] antialiased">
      <AppleNav />

      {/* Hero */}
      <section className="relative pt-24 md:pt-28 pb-10 md:pb-16 text-center overflow-hidden">
        <div className="max-w-[1100px] mx-auto px-3 md:px-8 pt-14 md:pt-20 space-y-5">
          <p className="text-current-teal text-[15px] md:text-[17px] font-medium tracking-tight">For Organizations</p>
          <h1
            style={{ fontSize: 'clamp(36px, 6.5vw, 84px)', lineHeight: 1, letterSpacing: '-0.035em', fontWeight: 600, maxWidth: '15ch' }}
            className="mx-auto"
            data-testid="corporate-register-title"
          >
            Register your<br />organization.
          </h1>
          <p className="text-[#515154] mx-auto" style={{ fontSize: 'clamp(16px, 1.4vw, 20px)', lineHeight: 1.4, letterSpacing: '-0.005em', maxWidth: '620px' }}>
            Industry-specific leadership assessments, team analytics, and data export &mdash; built for Healthcare, IT, Finance, Education, Retail, and HR.
          </p>
        </div>
      </section>

      {/* Form tile */}
      <section className="relative pb-24 md:pb-36">
        <div className="max-w-[560px] mx-auto px-3 md:px-8">
          <div className="rounded-[28px] p-8 md:p-10 bg-white border border-black/5" style={{ boxShadow: '0 1px 0 rgba(0,0,0,0.02)' }}>
            <form onSubmit={handleSubmit} className="space-y-5">
              <AppleInput
                label="Organization name"
                id="org-name"
                type="text"
                placeholder="Acme Corporation"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                tid="org-name-input"
              />

              <div className="space-y-2">
                <label htmlFor="industry" className="text-[13px] font-medium text-[#1d1d1f] tracking-tight block">Industry</label>
                <select
                  id="industry"
                  data-testid="industry-select"
                  value={form.industry}
                  onChange={(e) => setForm({ ...form, industry: e.target.value })}
                  required
                  className="w-full px-4 py-3 rounded-xl bg-[#f5f5f7] border border-transparent text-[15px] text-[#1d1d1f] focus:border-current-teal focus:bg-white focus:outline-none transition-colors appearance-none cursor-pointer"
                  style={{ backgroundImage: "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2386868b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center', paddingRight: '40px' }}
                >
                  <option value="" disabled>Select your industry</option>
                  {INDUSTRIES.map((ind) => (
                    <option key={ind.value} value={ind.value}>{ind.label}</option>
                  ))}
                </select>
              </div>

              <div className="pt-4 border-t border-black/5">
                <p className="text-[13px] font-semibold text-current-teal mb-5 tracking-tight">Admin account</p>
                <div className="space-y-5">
                  <AppleInput
                    label="Your name"
                    id="admin-name"
                    type="text"
                    placeholder="John Doe"
                    value={form.admin_name}
                    onChange={(e) => setForm({ ...form, admin_name: e.target.value })}
                    required
                    tid="admin-name-input"
                  />
                  <AppleInput
                    label="Admin email"
                    id="admin-email"
                    type="email"
                    placeholder="admin@company.com"
                    value={form.admin_email}
                    onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                    required
                    tid="admin-email-input"
                  />
                  <AppleInput
                    label="Password"
                    id="admin-password"
                    type="password"
                    placeholder="Minimum 6 characters"
                    value={form.admin_password}
                    onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
                    required
                    tid="admin-password-input"
                  />
                  <AppleInput
                    label="Confirm password"
                    id="confirm-password"
                    type="password"
                    placeholder="••••••••"
                    value={form.confirm_password}
                    onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                    required
                    tid="confirm-password-input"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="register-org-btn"
                className="w-full mt-6 px-6 py-3 rounded-full text-[15px] font-medium bg-current-teal hover:bg-ice-mist text-white transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Register organization
              </button>
            </form>

            <p className="text-center text-[13px] text-[#6e6e73] mt-6">
              Already have an account?{' '}
              <Link to="/login" className="text-current-teal hover:text-ice-mist font-medium">Sign in</Link>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CorporateRegister;
